import type { LimitUsage, PlanLimits, TenantStatus } from '@sm/api';
import { limitRatio, limitTone, type PlanLimitKey } from '@sm/core';

import type { Tone } from '@/components/ui/cx';

/** Iskorištenost za ključ limita (`projectsPerMonth` → `projectsThisMonth`, `storageGb` → GB iz bajtova). */
export function usageFor(key: PlanLimitKey, usage: LimitUsage | undefined): number {
  if (!usage) return 0;
  switch (key) {
    case 'users':
      return usage.users;
    case 'surveyors':
      return usage.surveyors;
    case 'projectsPerMonth':
      return usage.projectsThisMonth;
    case 'storageGb':
      return Math.round((usage.storageBytes / 1024 ** 3) * 10) / 10;
    case 'devices':
      return usage.devices;
  }
}

export interface LimitView {
  key: PlanLimitKey;
  used: number;
  limit: number | null;
  ratio: number | null;
  tone: 'ok' | 'caution' | 'danger';
  /** Širina trake u procentima (0–100). */
  pct: number;
}

export function limitView(
  key: PlanLimitKey,
  limits: PlanLimits | undefined,
  usage: LimitUsage | undefined,
): LimitView {
  const used = usageFor(key, usage);
  const limit = limits?.[key] ?? null;
  const ratio = limitRatio(used, limit);
  const pct = ratio == null ? 0 : Math.max(0, Math.min(100, Math.round(ratio * 100)));
  return { key, used, limit, ratio, tone: limitTone(ratio), pct };
}

export const LIMIT_TONE_BAR: Record<LimitView['tone'], string> = {
  ok: 'bg-ok',
  caution: 'bg-caution',
  danger: 'bg-danger',
};

export const TENANT_STATUS_TONE: Record<TenantStatus, Tone> = {
  pending_approval: 'caution',
  trial: 'info',
  active: 'ok',
  suspended: 'danger',
  cancelled: 'neutral',
};
